import { useEffect, useState } from "react";
import { Check, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import { PageTitle } from "@/components/Brand";

export default function AdvisorPricingQuotes({ lang }) {
  const [quotes, setQuotes] = useState([]);
  const [amounts, setAmounts] = useState({});
  const [notes, setNotes] = useState({});
  const [scopes, setScopes] = useState({});
  const [expiries, setExpiries] = useState({});
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const fa = lang === "fa";

  const load = () => api.get("/advisor/pricing-quotes")
    .then((r) => {
      setQuotes(r.data.quotes);
      setAmounts(Object.fromEntries(r.data.quotes.map((quote) => [quote.id, String(quote.amount_toman)])));
    })
    .catch((err) => setError(errorText(err, fa ? "دسترسی مشاور لازم است." : "Advisor access is required.")));

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const approve = async (quote) => {
  const scope = (scopes[quote.id] || "").split("\n").map(x => x.trim()).filter(Boolean);
  const validUntil = expiries[quote.id];
  if (!scope.length || !validUntil) { toast.error(fa ? "خدمات و تاریخ اعتبار را وارد کنید." : "Enter scope and expiry."); return; }

    const amount = Number(amounts[quote.id]);
    if (!Number.isInteger(amount) || amount <= 0) {
      toast.error(fa ? "مبلغ معتبر به تومان وارد کنید." : "Enter a valid toman amount.");
      return;
    }
    setBusy(quote.id);
    try {
      await api.post(`/advisor/pricing-quotes/${quote.id}/approve`, { amount_toman: amount, note: notes[quote.id] || "", scope, valid_until: new Date(validUntil).toISOString() });
      toast.success(fa ? "پیشنهاد تأیید شد؛ پرداخت هنوز فعال نشده است." : "Quote approved; payment remains disabled.");
      load();
    } catch (err) { toast.error(errorText(err, fa ? "تأیید پیشنهاد انجام نشد." : "Could not approve the quote.")); }
    finally { setBusy(""); }
  };

  return (
    <main className="section-page wide advisor-pricing-page">
      <PageTitle eyebrow="NOORIN / ADVISOR" title={fa ? "تأیید پیشنهادهای قیمت" : "Approve price proposals"} sub={fa ? "پیشنهاد AI را پس از گفت‌وگو با کاربر در تلگرام بررسی، اصلاح و تأیید کنید." : "Review, revise and approve AI suggestions after discussing them with the customer on Telegram."} />
      {error && <p className="notice">{error}</p>}
      {!error && quotes.length === 0 && <p className="muted">{fa ? "پیشنهادی منتظر بررسی وجود ندارد." : "No proposals are waiting for review."}</p>}
      <div className="list advisor-quote-list">
        {quotes.map((quote) => <article className="list-item" key={quote.id}>
          <p className="eyebrow">{quote.business_name || quote.business_id} · {quote.tier}</p>
          <strong>{quote.amount_toman.toLocaleString()} {fa ? "تومان" : "toman"}</strong>
          <p>{quote.summary}</p>
          {quote.scope?.length > 0 && <ul>{quote.scope.map((item) => <li key={item}>{item}</li>)}</ul>}
          <div className="advisor-quote-controls">
            <label>{fa ? "مبلغ تأییدشده (تومان)" : "Approved amount (toman)"}<input type="number" min="1" value={amounts[quote.id] || ""} onChange={(event) => setAmounts({ ...amounts, [quote.id]: event.target.value })} /></label>
            <label>{fa ? "یادداشت مشاور" : "Advisor note"}<textarea value={notes[quote.id] || ""} onChange={(event) => setNotes({ ...notes, [quote.id]: event.target.value })} /></label>
            <label>دامنه خدمات (هر خدمت در یک خط)<textarea value={scopes[quote.id] || ""} onChange={e => setScopes({...scopes, [quote.id]: e.target.value})} /></label>
          <label>تاریخ اعتبار پیشنهاد<input type="datetime-local" value={expiries[quote.id] || ""} onChange={e => setExpiries({...expiries, [quote.id]: e.target.value})} /></label>
          <a className="quiet-button telegram-link" href="https://t.me/Noorin_strategy" target="_blank" rel="noreferrer"><MessageCircle size={16} />{fa ? "گفت‌وگو در تلگرام" : "Discuss on Telegram"}</a>
            <button className="primary-button" onClick={() => approve(quote)} disabled={busy === quote.id}><Check size={16} />{busy === quote.id ? "…" : (fa ? "تأیید قیمت" : "Approve price")}</button>
          </div>
        </article>)}
      </div>
    </main>
  );
}
